/* eslint-disable no-unused-vars */
import {
	createUserWithEmailAndPassword,
	signInWithEmailAndPassword,
	onAuthStateChanged,
	confirmPasswordReset,
	sendEmailVerification,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { FirebaseAuth, FirebaseFunction } from './index';
import { SINGLE_ACTIVE_LOGIN_ENABLED } from '../constant/features';
import { loginErrorHandler } from '../helper/errorParser';
import { readDocumentOnce } from '../service';
import {
	beginPendingAuthSessionTakeover,
	clearStoredAuthSession,
	getStoredAuthSessionId,
	finalizeStoredAuthSession,
} from '../helper/authSessionStorage';
import {
	claimActiveAuthSession,
	releaseActiveAuthSession,
} from '../service/authSessionService';

const getClientInfo = () => ({
	platform: 'web',
	userAgent: typeof navigator !== 'undefined' ? navigator.userAgent || '' : '',
});

const isMissingDocumentResult = (result) => result?.code === 'data-not-exist';

const getProfileReadErrorMessage = (result) =>
	result?.message && result.code !== 'default'
		? result.message
		: 'Gagal memuat profil akun. Periksa koneksi lalu coba lagi.';

// Flag untuk mencegah onAuthStateChanged melakukan duplicate work
// saat loginWithCredentials sedang berjalan
let loginInProgress = false;

export const registerWithCredentials = async ({ email, password }) => {
	try {
		const resp = await createUserWithEmailAndPassword(
			FirebaseAuth,
			email,
			password
		);

		if (!resp.user) {
			throw new Error('register-failed');
		}

		return { code: 'SUCCESS', user: resp.user };
	} catch (error) {
		return error;
	}
};

export const loginWithCredentials = async ({ email, password }) => {
	loginInProgress = true;
	const sessionId = SINGLE_ACTIVE_LOGIN_ENABLED
		? beginPendingAuthSessionTakeover()
		: null;
	let signedIn = false;
	let signedInUser = null;

	try {
		const res = await signInWithEmailAndPassword(FirebaseAuth, email, password);
		signedIn = true;
		signedInUser = res.user;

		if (!res.user) {
			throw new Error('auth/login-failed');
		}

		// Ensure auth token is ready before the first Firestore read.
		await res.user.getIdToken(true);

		const userData = await readDocumentOnce('user', res.user.uid);
		if (!userData.data) {
			clearStoredAuthSession();
			if (signedIn && FirebaseAuth.currentUser) {
				try {
					await FirebaseAuth.signOut();
				} catch (signOutError) {
					// Best effort cleanup
				}
			}

			if (!isMissingDocumentResult(userData)) {
				return getProfileReadErrorMessage(userData);
			}

			return {
				status: 'REGISTRATION_INCOMPLETE',
				email: signedInUser?.email || email,
				hasLinkedPhone: Boolean(signedInUser?.phoneNumber),
			};
		}

		// Fetch initial data from Cloud Function
		let initialData = null;
		try {
			const getInitialDataFn = httpsCallable(
				FirebaseFunction,
				'getInitialData'
			);
			const result = await getInitialDataFn({
				latihanType: userData.data.latihanType,
			});
			initialData = result.data?.data ?? null;
		} catch (initialDataError) {
			// Failed to fetch initial data on login
		}

		if (SINGLE_ACTIVE_LOGIN_ENABLED && sessionId) {
			await claimActiveAuthSession(sessionId, getClientInfo());
			finalizeStoredAuthSession({
				userId: res.user.uid,
				sessionId,
			});
		} else {
			clearStoredAuthSession();
		}

		return {
			userId: res.user.uid,
			name: res.user.displayName,
			profilePicture: res.user.photoURL,
			...userData.data,
			phoneVerified: userData.data.phoneVerified === true,
			phoneVerificationRequired:
				userData.data.phoneVerificationRequired === true,
			verificationLevel: userData.data.verificationLevel || 'basic',
			menuConfig: initialData?.menuConfig ?? null,
			testTypes: initialData?.testTypes ?? null,
			membershipStatus: initialData?.membershipStatus ?? null,
			hasUjian: initialData?.hasUjian ?? true,
			scoringSummary: initialData?.scoringSummary ?? null,
		};
	} catch (error) {
		clearStoredAuthSession();
		if (signedIn && FirebaseAuth.currentUser) {
			try {
				await FirebaseAuth.signOut();
			} catch (signOutError) {
				// Best effort cleanup
			}
		}

		const errorCode = error?.code || error?.message || 'auth/general-error';
		return loginErrorHandler(errorCode);
	} finally {
		loginInProgress = false;
	}
};

export const onAuthStateHasChanged = (setSession) => {
	onAuthStateChanged(FirebaseAuth, async (user) => {
		if (!user) {
			setSession({
				status: 'no-authenticated',
				userId: null,
				role: null,
			});
			return;
		}

		// loginWithCredentials sedang handle session — skip untuk menghindari
		// duplicate Firestore read + getInitialData Cloud Function call
		if (loginInProgress) return;

		try {
			const userData = await readDocumentOnce('user', user.uid);
			if (!userData.data) {
				// User ada di Firebase Auth tapi belum selesai registrasi (belum OTP)
				setSession({ status: 'no-authenticated', userId: null, role: null });
				return;
			}

			// Fetch initial data from Cloud Function (non-blocking)
			let initialData = null;
			try {
				const getInitialDataFn = httpsCallable(
					FirebaseFunction,
					'getInitialData'
				);
				const result = await getInitialDataFn({
					latihanType: userData.data.latihanType,
				});
				initialData = result.data?.data ?? null;
			} catch (initialDataError) {
				// Failed to fetch initial data
			}

			// Gunakan functional setState agar tidak menimpa session
			// yang sudah di-set oleh loginWithCredentials (race condition)
			setSession((prev) => {
				if (prev.status === 'authenticated' && prev.userId === user.uid) {
					return prev;
				}
				return {
					status: 'authenticated',
					userId: user.uid,
					name: userData.data.name,
					profilePicture: user.photoURL,
					latihanType: userData.data.latihanType,
					birthDate: userData.data.birthDate,
					gender: userData.data.gender,
					membershipType: userData.data.membershipType,
					phoneVerified: userData.data.phoneVerified === true,
					phoneVerificationRequired:
						userData.data.phoneVerificationRequired === true,
					verificationLevel: userData.data.verificationLevel || 'basic',
					menuConfig: initialData?.menuConfig ?? null,
					testTypes: initialData?.testTypes ?? null,
					membershipStatus: initialData?.membershipStatus ?? null,
					hasUjian: initialData?.hasUjian ?? true,
					scoringSummary: initialData?.scoringSummary ?? null,
				};
			});
		} catch (error) {
			// Jika Firestore gagal (network error, dll), jangan biarkan app stuck
			// Tapi jangan timpa session yang sudah authenticated
			setSession((prev) => {
				if (prev.status === 'authenticated') return prev;
				return { status: 'no-authenticated', userId: null, role: null };
			});
		}
	});
};

export const refreshCurrentUserSession = async () => {
	const user = FirebaseAuth.currentUser;
	if (!user) return null;

	const userData = await readDocumentOnce('user', user.uid);
	if (!userData.data) return null;

	let initialData = null;
	try {
		const getInitialDataFn = httpsCallable(FirebaseFunction, 'getInitialData');
		const result = await getInitialDataFn({
			latihanType: userData.data.latihanType,
		});
		initialData = result.data?.data ?? null;
	} catch (e) {
		// Failed to fetch initial data on refresh
	}

	return {
		status: 'authenticated',
		userId: user.uid,
		name: userData.data.name,
		profilePicture: user.photoURL,
		latihanType: userData.data.latihanType,
		birthDate: userData.data.birthDate,
		gender: userData.data.gender,
		membershipType: userData.data.membershipType,
		phoneVerified: userData.data.phoneVerified === true,
		phoneVerificationRequired: userData.data.phoneVerificationRequired === true,
		verificationLevel: userData.data.verificationLevel || 'basic',
		menuConfig: initialData?.menuConfig ?? null,
		testTypes: initialData?.testTypes ?? null,
		membershipStatus: initialData?.membershipStatus ?? null,
		hasUjian: initialData?.hasUjian ?? true,
		scoringSummary: initialData?.scoringSummary ?? null,
	};
};

export const logoutFirebase = async () => {
	const sessionId = getStoredAuthSessionId();

	if (SINGLE_ACTIVE_LOGIN_ENABLED && sessionId) {
		try {
			await releaseActiveAuthSession(sessionId);
		} catch (error) {
			// Best effort cleanup — tetap lanjut sign out walau release gagal.
		}
	}

	clearStoredAuthSession();
	await FirebaseAuth.signOut();
};

export const sendRegistrationEmailOtp = async () => {
	const fn = httpsCallable(FirebaseFunction, 'sendEmailOtp');
	return fn({});
};

export const verifyRegistrationEmailOtp = async (otpCode) => {
	const fn = httpsCallable(FirebaseFunction, 'verifyEmailOtp');
	return fn({ otpCode });
};

export const finalizeRegistrationProfile = async (payload) => {
	const fn = httpsCallable(FirebaseFunction, 'storeUserData');
	return fn(payload);
};

export const completePhoneVerification = async () => {
	const fn = httpsCallable(FirebaseFunction, 'completePhoneVerification');
	return fn({});
};

export const validateAffiliateCode = async (affiliateCode) => {
	const fn = httpsCallable(FirebaseFunction, 'validateAffiliateCode');
	return fn({ affiliateCode });
};

export const checkPhoneNumberAvailability = async (phoneNumber) => {
	const fn = httpsCallable(FirebaseFunction, 'checkPhoneNumberAvailability');
	const result = await fn({ phoneNumber });
	return result.data;
};

export const addAdminRole = async (uid, role) => {
	try {
		const assignUserRolesFunction = httpsCallable(
			FirebaseFunction,
			'assignUserRoles'
		);
		const resultCreateRole = await assignUserRolesFunction({ role, uid });
		return resultCreateRole;
	} catch (error) {
		return error;
	}
};

export const createUser = async (payload) => {
	try {
		const createuserFunction = httpsCallable(FirebaseFunction, 'createUser');
		return await createuserFunction(payload);
	} catch (error) {
		return error;
	}
};

export const getAllusers = async (payload) => {
	try {
		const getAllUserFunction = httpsCallable(FirebaseFunction, 'getAllusers');
		return await getAllUserFunction(payload);
	} catch (error) {
		return error;
	}
};

export const deleteUser = async (payload) => {
	try {
		const deleteUserFunction = httpsCallable(FirebaseFunction, 'deleteUser');
		return await deleteUserFunction(payload);
	} catch (error) {
		return error;
	}
};

export const editUserInfo = async (payload) => {
	try {
		const editUserInfoFunction = httpsCallable(
			FirebaseFunction,
			'editUserInfo'
		);
		return await editUserInfoFunction(payload);
	} catch (error) {
		return error;
	}
};

export const handleForgotPassword = async (email) => {
	const fn = httpsCallable(FirebaseFunction, 'sendUserPasswordReset');
	const result = await fn({ email });
	const payload = result?.data;
	if (payload?.code && payload.code !== 'SUCCESS') {
		throw new Error(payload.message || 'reset-password-failed');
	}
	return payload;
};

export const handleResetPassword = async (oobCode, newPassword) => {
	await confirmPasswordReset(FirebaseAuth, oobCode, newPassword);
};

export const resendVerificationEmail = async ({ email, password }) => {
	try {
		const res = await signInWithEmailAndPassword(FirebaseAuth, email, password);
		if (res.user && !res.user.emailVerified) {
			await sendEmailVerification(res.user);
			return { success: true };
		}
		// Already verified — nothing to resend
		return { success: false, alreadyVerified: true };
	} catch (error) {
		return { success: false, message: error?.message };
	}
};
